import { Controller } from '@hotwired/stimulus'
import 'cally'

// I-DP-01: every <input type="date"> becomes a text field in Czech format („1. 5. 2027“) with a Cally calendar popover.
// Attached once to <html> by application.ts, so views stay untouched: a MutationObserver re-enhances inputs added by
// Turbo frames/streams or reset to type=date by a morph refresh. The input keeps its id/name/label; on submit the
// `formdata` event swaps the Czech text for ISO, so params are unchanged. Typed d. m. yyyy, d.m.yyyy and ISO all parse.
// Texts come from cs.yml via <meta name="datepicker-i18n"> (layouts/_head).

type Texts = Record<'calendar' | 'today' | 'previous_month' | 'next_month' | 'placeholder' | 'invalid' | 'too_early' | 'too_late', string>

const SELECTOR = 'input[data-datepicker]'
const CZECH = /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/
const ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})$/

// Returns yyyy-mm-dd for a real calendar date, '' for blank, null for anything unparseable (31. 2. included).
export function toIso(text: string): string | null {
  const value = text.trim()
  if (value === '') return ''
  const czech = CZECH.exec(value)
  const iso = ISO.exec(value)
  const [year, month, day] = czech ? [czech[3], czech[2], czech[1]] : iso ? [iso[1], iso[2], iso[3]] : []
  if (!year || !month || !day) return null
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  if (date.getUTCMonth() !== Number(month) - 1 || date.getUTCDate() !== Number(day)) return null
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

export function toCzech(iso: string): string {
  const [year, month, day] = iso.split('-')
  return year && month && day ? `${Number(day)}. ${Number(month)}. ${year}` : iso
}

function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

function readTexts(): Texts | null {
  const content = document.querySelector<HTMLMetaElement>('meta[name="datepicker-i18n"]')?.content
  return content ? (JSON.parse(content) as Texts) : null
}

export default class DatepickerController extends Controller<HTMLElement> {
  private texts: Texts | null = null
  private observer: MutationObserver | null = null
  private popover: HTMLElement | null = null
  private input: HTMLInputElement | null = null
  private resizeObserver: ResizeObserver | null = null
  private monthYear: Intl.DateTimeFormat | null = null

  override connect(): void {
    this.texts = readTexts()
    this.enhanceAll()
    this.observer = new MutationObserver(() => this.enhanceAll())
    this.observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['type'] })
    document.addEventListener('formdata', this.onFormData, true)
    document.addEventListener('change', this.onChange, true)
    document.addEventListener('input', this.onInput, true)
    document.addEventListener('click', this.onClick)
    document.addEventListener('keydown', this.onKeydown)
    document.addEventListener('focusin', this.onFocusin)
    document.addEventListener('turbo:before-cache', this.close)
    document.addEventListener('scroll', this.reposition, true)
    window.addEventListener('resize', this.close)
  }

  override disconnect(): void {
    this.observer?.disconnect()
    document.removeEventListener('formdata', this.onFormData, true)
    document.removeEventListener('change', this.onChange, true)
    document.removeEventListener('input', this.onInput, true)
    document.removeEventListener('click', this.onClick)
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('focusin', this.onFocusin)
    document.removeEventListener('turbo:before-cache', this.close)
    document.removeEventListener('scroll', this.reposition, true)
    window.removeEventListener('resize', this.close)
    this.close()
  }

  private enhanceAll(): void {
    for (const input of document.querySelectorAll<HTMLInputElement>('input[type="date"]')) {
      // A date input's value is ISO or ''; after a morph reset type=date the Czech text was sanitised to '', so the
      // server-rendered attribute is the truth.
      const iso = input.value || toIso(input.getAttribute('value') ?? '') || ''
      input.type = 'text'
      input.dataset['datepicker'] = ''
      input.autocomplete = 'off'
      input.setAttribute('aria-haspopup', 'dialog')
      if (!input.placeholder && this.texts) input.placeholder = this.texts.placeholder
      input.value = toCzech(iso)
      this.validate(input)
    }
  }

  private readonly onFormData = (event: FormDataEvent): void => {
    const form = event.target
    for (const input of document.querySelectorAll<HTMLInputElement>(SELECTOR)) {
      if (input.form !== form || !input.name || input.matches(':disabled')) continue
      const iso = toIso(input.value)
      if (iso !== null) event.formData.set(input.name, iso) // unparseable text goes as typed; the server rejects it
    }
  }

  // Programmatic or typed changes: normalise the shown text („2027-05-01“ → „1. 5. 2027“).
  private readonly onChange = (event: Event): void => {
    const input = event.target
    if (!(input instanceof HTMLInputElement) || !input.matches(SELECTOR)) return
    const iso = toIso(input.value)
    if (iso) input.value = toCzech(iso)
    this.validate(input)
  }

  private readonly onInput = (event: Event): void => {
    if (event.target instanceof HTMLInputElement && event.target.matches(SELECTOR)) this.validate(event.target)
  }

  private readonly onClick = (event: MouseEvent): void => {
    if (event.target instanceof HTMLInputElement && event.target.matches(SELECTOR)) this.open(event.target, false)
  }

  private readonly onKeydown = (event: KeyboardEvent): void => {
    const input = event.target
    if (event.key !== 'ArrowDown' || !(input instanceof HTMLInputElement) || !input.matches(SELECTOR)) return
    event.preventDefault()
    this.open(input, true)
  }

  // Tabbing away from the field and its calendar closes it (outside clicks and Escape are the popover's light dismiss).
  private readonly onFocusin = (event: FocusEvent): void => {
    const target = event.target
    if (this.popover && target instanceof Node && target !== this.input && !this.popover.contains(target)) this.close()
  }

  // type=text drops native min/max/format checks; mirror them with a custom validity message.
  private validate(input: HTMLInputElement): void {
    const iso = toIso(input.value)
    const texts = this.texts
    let message = ''
    if (iso === null) message = texts?.invalid ?? ''
    else if (iso && input.min && iso < input.min) message = texts?.too_early.replace('%{date}', toCzech(input.min)) ?? ''
    else if (iso && input.max && iso > input.max) message = texts?.too_late.replace('%{date}', toCzech(input.max)) ?? ''
    input.setCustomValidity(message)
  }

  private open(input: HTMLInputElement, focusCalendar: boolean): void {
    if (input.readOnly || input.matches(':disabled')) return
    if (this.input !== input) this.close()
    if (!this.popover) this.build(input)
    const popover = this.popover
    const calendar = popover?.querySelector<HTMLElement & { value: string, focus(): void }>('calendar-date')
    if (!popover || !calendar) return

    if (!popover.matches(':popover-open')) {
      const iso = toIso(input.value) || ''
      calendar.value = iso
      calendar.setAttribute('focused-date', iso || todayIso())
      this.setHeading(new Date(`${iso || todayIso()}T00:00:00Z`))
      popover.showPopover()
      this.reposition()
    }
    if (focusCalendar) requestAnimationFrame(() => calendar.focus())
  }

  // Lives next to the input's dialog (a modal makes everything outside it inert) or in <body>; removed on close.
  private build(input: HTMLInputElement): void {
    const texts = this.texts
    const popover = document.createElement('div')
    popover.popover = 'auto'
    popover.className = 'datepicker m-0 w-72 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-box border border-base-300 bg-base-100 p-2 text-base-content shadow-lg'
    popover.setAttribute('role', 'dialog')
    popover.setAttribute('aria-label', texts?.calendar ?? '') // not the field label: getByLabel(field) must stay unique

    const locale = document.documentElement.lang || 'cs'
    const calendar = document.createElement('calendar-date')
    calendar.setAttribute('locale', locale)
    calendar.setAttribute('first-day-of-week', '1')
    if (input.min) calendar.setAttribute('min', input.min)
    if (input.max) calendar.setAttribute('max', input.max)
    // One header line „Říjen 2026“ instead of Cally's year heading over a month heading; Cally's own visually hidden
    // live region still announces the month, so this copy is aria-hidden.
    const heading = document.createElement('span')
    heading.slot = 'heading'
    heading.setAttribute('aria-hidden', 'true')
    calendar.append(this.navButton('previous', '‹', texts?.previous_month), heading, this.navButton('next', '›', texts?.next_month),
      document.createElement('calendar-month'))
    calendar.addEventListener('change', () => this.pick((calendar as HTMLElement & { value: string }).value))
    calendar.addEventListener('focusday', (event) => this.setHeading((event as CustomEvent<Date>).detail)) // ‹ › and arrow keys
    this.monthYear = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })

    const today = document.createElement('button')
    today.type = 'button'
    today.className = 'btn btn-ghost btn-xs mt-1 w-full'
    today.textContent = texts?.today ?? ''
    const now = todayIso()
    today.disabled = (!!input.min && now < input.min) || (!!input.max && now > input.max)
    today.addEventListener('click', () => this.pick(now))

    popover.append(calendar, today)
    popover.addEventListener('toggle', (event) => {
      if ((event as ToggleEvent).newState === 'closed') this.removePopover()
    })
    popover.addEventListener('keydown', (event) => { if (event.key === 'Escape') input.focus() })
    ;(input.closest('dialog') ?? document.body).append(popover)
    this.popover = popover
    this.input = input
    // Cally renders after showPopover() and a month can have 5 or 6 weeks: place it again whenever its size changes.
    this.resizeObserver = new ResizeObserver(() => this.reposition())
    this.resizeObserver.observe(calendar)
  }

  private setHeading(date: Date): void {
    const heading = this.popover?.querySelector('[slot="heading"]')
    const text = this.monthYear?.format(date) ?? ''
    if (heading) heading.textContent = text.charAt(0).toLocaleUpperCase() + text.slice(1)
  }

  private navButton(slot: string, glyph: string, label: string | undefined): HTMLElement {
    const span = document.createElement('span')
    span.slot = slot
    span.setAttribute('role', 'img')
    span.setAttribute('aria-label', label ?? slot)
    span.textContent = glyph
    return span
  }

  // Anchored under the field; flipped above when it does not fit below. If neither side fits, the roomier one gets a
  // capped, scrollable popover, so it is never cut off by the viewport.
  private readonly reposition = (): void => {
    const input = this.input
    const popover = this.popover
    if (!input || !popover?.matches(':popover-open')) return
    const gap = 4
    const margin = 8
    const field = input.getBoundingClientRect()
    const natural = popover.scrollHeight + popover.offsetHeight - popover.clientHeight // content + borders, uncapped
    const spaceBelow = window.innerHeight - field.bottom - gap - margin
    const spaceAbove = field.top - gap - margin
    const below = natural <= spaceBelow || spaceBelow >= spaceAbove
    const height = Math.min(natural, below ? spaceBelow : spaceAbove)
    const top = below ? field.bottom + gap : field.top - gap - height
    const left = Math.max(margin, Math.min(field.left, window.innerWidth - popover.offsetWidth - margin))
    popover.dataset['placement'] = below ? 'bottom' : 'top'
    popover.style.setProperty('position', 'fixed')
    popover.style.setProperty('max-height', `${Math.max(height, 0)}px`)
    popover.style.setProperty('inset', `${top}px auto auto ${left}px`)
  }

  private pick(iso: string): void {
    const input = this.input
    if (!input) return
    input.value = toCzech(iso)
    this.validate(input)
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
    this.close()
    input.focus()
  }

  private readonly close = (): void => {
    if (this.popover?.matches(':popover-open')) this.popover.hidePopover()
    this.removePopover()
  }

  private removePopover(): void {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.popover?.remove()
    this.popover = null
    this.input = null
  }
}
